import { useApi } from '@hero/context';

import type { UseAccountApi, UseAccountByPairApi, UseAccountListApi } from './types';
import { useInfiniteQueryAPI, useQueryAPI } from '../../utils';

/**
 * Get a single bilateral `Account` by id.
 *
 * Hook API: {@link UseAccountApi}
 */
export const useAccountApi: UseAccountApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'account',
    apiFn: api.payment.getAccount,
    query: undefined,
    singleIdQuery: id,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * Infinite list of the viewer's bilateral `Account` documents.
 *
 * Hook API: {@link UseAccountListApi}
 */
export const useAccountListApi: UseAccountListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'account',
    apiFn: api.payment.getAccounts,
    query,
    apiConfig,
  });
};

/**
 * Exact-pair lookup. Returns `null` if no Account document
 * exists yet between the two parties.
 *
 * The query is disabled while either `partyAId` or `partyBId`
 * is missing — common during a CRM open where the linked
 * counterparty hasn't loaded yet.
 *
 * Hook API: {@link UseAccountByPairApi}
 */
export const useAccountByPairApi: UseAccountByPairApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'account',
    apiFn: api.payment.getAccountByPair,
    query,
    apiConfig,
    isEnabled: () => Boolean(query?.partyAId) && Boolean(query?.partyBId),
  });
};
